import { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SITE_NAME = "Tweaq";
const SITE_URL = "https://tweaq.com";
const DEFAULT_IMAGE = `${SITE_URL}/tweaq-share.png`;
const TWITTER_SITE = "@tweaq";
const DEFAULT_LANG = "en";

type RouteMetadata = {
    titleKey: string;
    descriptionKey: string;
    path?: string;
    type?: "website" | "article";
    noindex?: boolean;
};

const ROUTE_METADATA: Record<string, RouteMetadata> = {
    "/": {
        titleKey: "seo.home.title",
        descriptionKey: "seo.home.description",
        path: "/",
    },
    "/about": {
        titleKey: "seo.about.title",
        descriptionKey: "seo.about.description",
        path: "/about",
    },
    "/industries/logistics": {
        titleKey: "seo.logistics.title",
        descriptionKey: "seo.logistics.description",
        path: "/industries/logistics",
    },
    "/industries/buildings": {
        titleKey: "seo.buildings.title",
        descriptionKey: "seo.buildings.description",
        path: "/industries/buildings",
    },
    "/industries/manufacturing": {
        titleKey: "seo.manufacturing.title",
        descriptionKey: "seo.manufacturing.description",
        path: "/industries/manufacturing",
    },
    "/solutions/sim": {
        titleKey: "seo.sim.title",
        descriptionKey: "seo.sim.description",
        path: "/solutions/sim",
    },
    "/solutions/ops": {
        titleKey: "seo.ops.title",
        descriptionKey: "seo.ops.description",
        path: "/solutions/ops",
    },
    "/careers": {
        titleKey: "seo.careers.title",
        descriptionKey: "seo.careers.description",
        path: "/careers",
    },
    "/careers/business-developer-fleet-electrification": {
        titleKey: "seo.roles.bizDev.title",
        descriptionKey: "seo.roles.bizDev.description",
        path: "/careers/business-developer-fleet-electrification",
    },
    "/careers/energy-solutions-engineer": {
        titleKey: "seo.roles.energySolutions.title",
        descriptionKey: "seo.roles.energySolutions.description",
        path: "/careers/energy-solutions-engineer",
    },
    "/careers/software-engineer": {
        titleKey: "seo.roles.softwareEngineer.title",
        descriptionKey: "seo.roles.softwareEngineer.description",
        path: "/careers/software-engineer",
    },
    "/contact": {
        titleKey: "seo.contact.title",
        descriptionKey: "seo.contact.description",
        path: "/contact",
    },
    "/privacy": {
        titleKey: "seo.privacy.title",
        descriptionKey: "seo.privacy.description",
        path: "/privacy",
    },
    "/terms": {
        titleKey: "seo.terms.title",
        descriptionKey: "seo.terms.description",
        path: "/terms",
    },
    "/events": {
        titleKey: "seo.events.title",
        descriptionKey: "seo.events.description",
        path: "/events",
    },
    "/events/*/registration": {
        titleKey: "seo.events.registration.title",
        descriptionKey: "seo.events.registration.description",
        noindex: true,
    },
    "/events/*/confirmed": {
        titleKey: "seo.events.confirmed.title",
        descriptionKey: "seo.events.confirmed.description",
        noindex: true,
    },
    "/events/*/received": {
        titleKey: "seo.events.received.title",
        descriptionKey: "seo.events.received.description",
        noindex: true,
    },
    "/events/*/cancelled": {
        titleKey: "seo.events.cancelled.title",
        descriptionKey: "seo.events.cancelled.description",
        noindex: true,
    },
    "/events/*/declined": {
        titleKey: "seo.events.declined.title",
        descriptionKey: "seo.events.declined.description",
        noindex: true,
    },
    "*": {
        titleKey: "seo.notFound.title",
        descriptionKey: "seo.notFound.description",
        type: "website",
        noindex: true,
    },
};

const SUPPORTED_LANGS = ["en", "nl"];

/** Strip leading /:lang segment from pathname and normalize trailing slashes. */
const stripLangPrefix = (pathname: string): string => {
    const withoutLang = pathname.replace(/^\/(en|nl)/, "");
    const path = withoutLang || "/";
    return path === "/" ? path : path.replace(/\/+$/, "");
};

/** Extract the language from the pathname, falling back to provided default. */
const getLangFromPath = (pathname: string, fallback: string): string => {
    const match = pathname.match(/^\/(en|nl)/);
    return match ? match[1] : fallback;
};

const upsertMetaByName = (name: string, content: string) => {
    let element = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;

    if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
    }

    element.setAttribute("content", content);
};

const upsertMetaByProperty = (property: string, content: string) => {
    let element = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;

    if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
    }

    element.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
    let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;

    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
    }

    element.setAttribute("href", href);
};

const upsertHreflang = (hreflang: string, href: string) => {
    let element = document.head.querySelector(
        `link[rel="alternate"][hreflang="${hreflang}"]`
    ) as HTMLLinkElement | null;

    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", "alternate");
        element.setAttribute("hreflang", hreflang);
        document.head.appendChild(element);
    }

    element.setAttribute("href", href);
};

const getLocale = (language: string) => (language.startsWith("nl") ? "nl_NL" : "en_US");

/**
 * Resolve route metadata for a given path, supporting dynamic event segments.
 * Event paths follow the pattern /events/:slug and /events/:slug/:outcome.
 */
const resolveRouteMetadata = (pagePath: string): RouteMetadata => {
    if (ROUTE_METADATA[pagePath]) return ROUTE_METADATA[pagePath];

    const eventMatch = pagePath.match(/^\/events\/[^/]+(?:\/(.+))?$/);
    if (eventMatch) {
        const sub = eventMatch[1]; // e.g. "confirmed", "received", "apply", undefined
        if (sub === "confirmed") return ROUTE_METADATA["/events/*/confirmed"];
        if (sub === "received") return ROUTE_METADATA["/events/*/received"];
        if (sub === "cancelled") return ROUTE_METADATA["/events/*/cancelled"];
        if (sub === "declined") return ROUTE_METADATA["/events/*/declined"];
        // covers: no sub-path, "apply", "preview"
        return ROUTE_METADATA["/events/*/registration"];
    }

    return ROUTE_METADATA["*"];
};

const getAbsoluteUrl = (path: string) => new URL(path || "/", SITE_URL).toString();

const ORG_SCHEMA_SCRIPT_ID = "tweaq-org-schema";
const WEBPAGE_SCHEMA_SCRIPT_ID = "tweaq-webpage-schema";

const ORGANIZATION_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Tweaq",
    url: "https://tweaq.com",
    logo: "https://tweaq.com/logo.png",
    description:
        "Tweaq helps organizations sync their operations with real-world rhythms and constraints, so you cut cost and peaks, protect continuity, and avoid waste.",
    foundingDate: "2025",
    address: {
        "@type": "PostalAddress",
        streetAddress: "High Tech Campus 27",
        postalCode: "5656 AE",
        addressLocality: "Eindhoven",
        addressCountry: "NL",
    },
    contactPoint: {
        "@type": "ContactPoint",
        email: "info@tweaq.com",
        contactType: "customer service",
        telephone: "+31854018725",
    },
    sameAs: ["https://tweaq.com"],
    makesOffer: {
        "@type": "Offer",
        itemOffered: {
            "@type": "Service",
            name: "Intelligent Systems",
            description:
                "Intelligent software and advisory services to help organizations optimize energy usage, reduce demand peaks, integrate renewables, and improve operational sustainability",
        },
    },
};

const RouteSeo = () => {
    const location = useLocation();
    const { lang: paramLang } = useParams<{ lang?: string }>();
    const { t, i18n } = useTranslation();

    const language =
        paramLang ?? getLangFromPath(location.pathname, i18n.resolvedLanguage || i18n.language || "en");
    const pagePath = stripLangPrefix(location.pathname);
    const routeMetadata = resolveRouteMetadata(pagePath);
    const title = t(routeMetadata.titleKey);
    const description = t(routeMetadata.descriptionKey);

    useEffect(() => {
        // Canonical URL uses the language-prefixed path
        const canonicalPath = pagePath === "/" ? `/${language}` : `/${language}${pagePath}`;
        const absoluteUrl = getAbsoluteUrl(canonicalPath);

        document.documentElement.lang = language;
        document.title = title;

        upsertMetaByName("description", description);
        upsertMetaByName("author", SITE_NAME);
        upsertMetaByName("robots", routeMetadata.noindex ? "noindex, nofollow" : "index, follow");
        upsertMetaByProperty("og:title", title);
        upsertMetaByProperty("og:description", description);
        upsertMetaByProperty("og:type", routeMetadata.type ?? "website");
        upsertMetaByProperty("og:image", DEFAULT_IMAGE);
        upsertMetaByProperty("og:image:alt", title);
        upsertMetaByProperty("og:url", absoluteUrl);
        upsertMetaByProperty("og:site_name", SITE_NAME);
        upsertMetaByProperty("og:locale", getLocale(language));
        upsertMetaByProperty("og:image:width", "1200");
        upsertMetaByProperty("og:image:height", "630");

        // og:locale:alternate for each other language
        SUPPORTED_LANGS.filter((l) => l !== language).forEach((l) => {
            upsertMetaByProperty("og:locale:alternate", getLocale(l));
        });

        upsertMetaByName("twitter:title", title);
        upsertMetaByName("twitter:description", description);
        upsertMetaByName("twitter:card", "summary_large_image");
        upsertMetaByName("twitter:site", TWITTER_SITE);
        upsertMetaByName("twitter:image", DEFAULT_IMAGE);
        upsertMetaByName("twitter:image:alt", title);
        upsertLink("canonical", absoluteUrl);

        // hreflang alternate links for all language variants
        SUPPORTED_LANGS.forEach((l) => {
            const hreflangPath = pagePath === "/" ? `/${l}` : `/${l}${pagePath}`;
            upsertHreflang(l, getAbsoluteUrl(hreflangPath));
        });
        // x-default points to the English version
        const xDefaultPath = pagePath === "/" ? `/${DEFAULT_LANG}` : `/${DEFAULT_LANG}${pagePath}`;
        upsertHreflang("x-default", getAbsoluteUrl(xDefaultPath));

    }, [pagePath, language, location.pathname, t, routeMetadata]);

    const organizationSchema = useMemo(() => ({
        ...ORGANIZATION_SCHEMA,
        url: SITE_URL,
    }), []);

    const webpageSchema = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        description,
        url: getAbsoluteUrl(pagePath === "/" ? `/${language}` : `/${language}${pagePath}`),
        inLanguage: language,
        isPartOf: {
            "@type": "WebSite",
            name: SITE_NAME,
            url: SITE_URL,
        },
        primaryImageOfPage: {
            "@type": "ImageObject",
            url: DEFAULT_IMAGE,
        },
    }), [description, language, pagePath, title]);

    return (
        <>
            <script id={ORG_SCHEMA_SCRIPT_ID} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
            <script id={WEBPAGE_SCHEMA_SCRIPT_ID} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }} />
        </>
    );
};

export default RouteSeo;